import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import AdminSubscribers from './AdminSubscribers';

// Relative on purpose. In production nginx password-protects /api/admin and
// injects the X-API-Key header before proxying, so the key never reaches the
// browser. Locally, setupProxy.js does the same thing against :8001.
const ADMIN_API = '/api/admin';

const STATUSES = ['new', 'contacted', 'closed'];

const STATUS_STYLES = {
  new: 'bg-[#379F7F]/10 text-[#2b7d64] ring-[#379F7F]/30',
  contacted: 'bg-amber-50 text-amber-700 ring-amber-200',
  closed: 'bg-slate-100 text-slate-500 ring-slate-200',
};

function formatDate(value) {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function toCSV(rows) {
  const cols = ['createdAt', 'name', 'email', 'company', 'companyWebsite', 'status', 'message'];
  // Escape by doubling quotes - a message containing a comma or newline would
  // otherwise split into extra columns when opened in a spreadsheet.
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  return [cols.join(','), ...rows.map((r) => cols.map((c) => esc(r[c])).join(','))].join('\n');
}

function Enquiries() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [expanded, setExpanded] = useState(null);
  const [saving, setSaving] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${ADMIN_API}/contact`);
      setItems(data);
    } catch (e) {
      setError(
        e.response?.status === 401
          ? 'Not authorised. Reload the page and sign in again.'
          : 'Could not load submissions. Is the server running?'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    setSaving(id);
    const previous = items;
    // Optimistic: the table updates immediately and rolls back if the save fails.
    setItems((cur) => cur.map((i) => (i.id === id ? { ...i, status } : i)));
    try {
      await axios.patch(`${ADMIN_API}/contact/${id}`, { status });
      toast.success(`Marked ${status}`);
    } catch {
      setItems(previous);
      toast.error('Could not save that change');
    } finally {
      setSaving(null);
    }
  };

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((i) => {
      if (filter !== 'all' && i.status !== filter) return false;
      if (!q) return true;
      return [i.name, i.email, i.company, i.companyWebsite, i.message]
        .filter(Boolean).some((f) => f.toLowerCase().includes(q));
    });
  }, [items, query, filter]);

  const counts = useMemo(() => {
    const c = { all: items.length, new: 0, contacted: 0, closed: 0 };
    items.forEach((i) => { if (c[i.status] !== undefined) c[i.status] += 1; });
    return c;
  }, [items]);

  const download = () => {
    const blob = new Blob([toCSV(visible)], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `orboroi-enquiries-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Contact enquiries</h1>
            <p className="mt-1 text-sm text-slate-500">
              Everyone who has submitted the contact form, newest first.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={load}
              className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Refresh
            </button>
            <button
              onClick={download}
              disabled={!visible.length}
              className="rounded-md bg-[#379F7F] px-3 py-2 text-sm font-medium text-white hover:bg-[#2b7d64] disabled:opacity-40"
            >
              Download CSV
            </button>
          </div>
        </div>

        <div className="mb-4 flex flex-wrap items-center gap-2">
          {['all', ...STATUSES].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`rounded-full px-3 py-1.5 text-sm capitalize ring-1 transition ${
                filter === s
                  ? 'bg-slate-900 text-white ring-slate-900'
                  : 'bg-white text-slate-600 ring-slate-200 hover:bg-slate-100'
              }`}
            >
              {s} <span className="opacity-60">{counts[s] ?? 0}</span>
            </button>
          ))}
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, email, company or message…"
            className="ml-auto w-full min-w-[16rem] max-w-sm rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-[#379F7F] focus:outline-none focus:ring-1 focus:ring-[#379F7F]"
          />
        </div>

        {loading && <p className="py-16 text-center text-sm text-slate-500">Loading…</p>}

        {error && !loading && (
          <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {error}
          </div>
        )}

        {!loading && !error && !visible.length && (
          <div className="rounded-lg border border-dashed border-slate-300 bg-white py-16 text-center">
            <p className="text-sm text-slate-500">
              {items.length ? 'Nothing matches that filter.' : 'No enquiries yet.'}
            </p>
          </div>
        )}

        {!loading && !error && !!visible.length && (
          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-3 font-medium">Received</th>
                    <th className="px-4 py-3 font-medium">From</th>
                    <th className="px-4 py-3 font-medium">Company</th>
                    <th className="px-4 py-3 font-medium">Message</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {visible.map((i) => {
                    const open = expanded === i.id;
                    return (
                      <tr key={i.id} className="align-top hover:bg-slate-50/60">
                        <td className="whitespace-nowrap px-4 py-3 text-slate-500">
                          {formatDate(i.createdAt)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-slate-900">{i.name}</div>
                          <a href={`mailto:${i.email}`} className="text-[#379F7F] hover:underline">
                            {i.email}
                          </a>
                        </td>
                        <td className="px-4 py-3 text-slate-600">
                          {i.company || <span className="text-slate-300">—</span>}
                          {i.companyWebsite && (
                            <a
                              href={i.companyWebsite}
                              target="_blank"
                              rel="noreferrer"
                              className="block text-xs text-[#379F7F] hover:underline"
                            >
                              {i.companyWebsite.replace(/^https?:\/\//, '')}
                            </a>
                          )}
                        </td>
                        <td className="max-w-md px-4 py-3 text-slate-700">
                          <p className={open ? 'whitespace-pre-wrap' : 'line-clamp-2'}>
                            {i.message}
                          </p>
                          {i.message.length > 120 && (
                            <button
                              onClick={() => setExpanded(open ? null : i.id)}
                              className="mt-1 text-xs text-slate-400 hover:text-slate-600"
                            >
                              {open ? 'Show less' : 'Show more'}
                            </button>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium capitalize ring-1 ${STATUS_STYLES[i.status] || STATUS_STYLES.closed}`}
                          >
                            {i.status}
                          </span>
                          <div className="mt-2 flex flex-wrap gap-1">
                            {STATUSES.filter((s) => s !== i.status).map((s) => (
                              <button
                                key={s}
                                disabled={saving === i.id}
                                onClick={() => setStatus(i.id, s)}
                                className="rounded border border-slate-200 px-2 py-0.5 text-xs capitalize text-slate-500 hover:bg-slate-100 disabled:opacity-40"
                              >
                                {s}
                              </button>
                            ))}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <p className="mt-4 text-xs text-slate-400">
          Showing {visible.length} of {items.length}. These are real people's details — treat
          exported files accordingly.
        </p>
    </div>
  );
}

export default function Admin() {
  const [tab, setTab] = React.useState('enquiries');

  const tabClass = (t) =>
    `border-b-2 px-1 pb-3 text-sm font-medium transition ${
      tab === t
        ? 'border-[#379F7F] text-slate-900'
        : 'border-transparent text-slate-500 hover:text-slate-700'
    }`;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-8 flex gap-6 border-b border-slate-200">
          <button className={tabClass('enquiries')} onClick={() => setTab('enquiries')}>
            Contact enquiries
          </button>
          <button className={tabClass('subscribers')} onClick={() => setTab('subscribers')}>
            Newsletter subscribers
          </button>
        </div>
        {tab === 'enquiries' ? <Enquiries /> : <AdminSubscribers />}
      </div>
    </div>
  );
}
